// Turns a Firebase ID token into the session the app already stores.
// A token the API does not know is not a session.

import type { MeRecord, OrgRole, Session } from "../types/contract";
import { config } from "../config";
import {
  firebaseWebConfig,
  readyFirebaseUser,
  signInForIdToken,
  signOutFirebase,
} from "./firebaseAuth";

function sessionFromProfile(idToken: string, me: MeRecord): Session {
  const orgs = (me.orgs ?? []).map((org) => ({
    id: org.id,
    role: (org.role === "org_admin" ? "org_admin" : "org_member") as OrgRole,
  }));
  return {
    access_token: idToken,
    user_id: me.user_id,
    email: me.email,
    orgs,
    role: me.role || orgs[0]?.role || "org_member",
  };
}

async function profileFor(
  idToken: string,
): Promise<Session | "rejected" | "unavailable"> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}/users/me`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch {
    return "unavailable";
  }
  if (res.status === 401 || res.status === 403) return "rejected";
  if (!res.ok) return "unavailable";
  const body = (await res.json()) as { data?: MeRecord };
  const me = body.data;
  if (!me?.user_id) return "rejected";
  return sessionFromProfile(idToken, me);
}

export async function loginSession(
  email: string,
  password: string,
): Promise<{ session: Session; firebase: boolean }> {
  if (!firebaseWebConfig()) throw new Error("Sign-in is not configured");
  const idToken = await signInForIdToken(email, password);
  if (!idToken) throw new Error("That email and password did not sign in.");
  const profile = await profileFor(idToken);
  if (profile === "unavailable") {
    throw new Error("Can't reach the server. Try again in a moment.");
  }
  if (profile === "rejected") {
    const adopted = await adoptOrphanAccount(idToken, email);
    if (adopted) return { session: adopted, firebase: true };
    await signOutQuietly();
    throw new Error("That email and password did not sign in.");
  }
  return { session: profile, firebase: true };
}

async function signOutQuietly(): Promise<void> {
  try {
    await signOutFirebase();
  } catch {
    // The sign-in still failed.
  }
}

async function adoptOrphanAccount(
  idToken: string,
  email: string,
): Promise<Session | null> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}/auth/signup`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ui_mode: null }),
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: { data?: CreatedAccount };
  try {
    body = (await res.json()) as { data?: CreatedAccount };
  } catch {
    return null;
  }
  const created = body.data;
  if (!created?.user_id) return null;
  return {
    access_token: idToken,
    user_id: created.user_id,
    email: created.email || email,
    orgs: created.orgs ?? [],
    role: created.role || "org_admin",
  };
}

type CreatedAccount = {
  user_id?: string;
  email?: string | null;
  orgs?: Session["orgs"];
  role?: string;
};

export async function restoredFirebaseSession(): Promise<Session | null> {
  let user: Awaited<ReturnType<typeof readyFirebaseUser>>;
  try {
    user = await readyFirebaseUser();
  } catch {
    return null;
  }
  if (!user) return null;
  let idToken: string;
  try {
    idToken = await user.getIdToken();
  } catch {
    return null;
  }
  const profile = await profileFor(idToken);
  if (profile === "rejected") {
    try {
      await signOutFirebase();
    } catch {
      // Boot still continues with the stored refresh token.
    }
    return null;
  }
  if (profile === "unavailable") return null;
  return profile;
}
